import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

import PLCProvider from './PLCProvider';

const HMIHandleContext = createContext(null);

/**
 * PressHMI group-switching wrapper around PLCProvider.
 *
 * Owns the `groups` state (which subset of PLC symbols to poll), computes
 * the flat handle list from the schema's groupsMap, then renders PLCProvider
 * with that list.  Exposes groups management via useHMIHandles().
 *
 * Pages that don't need dynamic groups should use
 * PLCProvider directly with a pre-computed handle array.
 */
export default function HMIHandleProvider({
  plcUrl,
  pressId,
  groupsMap,
  debugPaths = null,
  children,
  test = false,
}) {
  const [groups, setGroups] = useState(['base']);

  const handles = useMemo(() => {
    const seen = new Map();
    groups
      .flatMap((name) => groupsMap[name] || [])
      .forEach((p) => seen.set(p.name, p));
    return [...seen.values()];
  }, [groupsMap, groups]);

  const ctx = useMemo(() => ({
    groups,
    setGroups,
  }), [groups]);

  return (
    <HMIHandleContext.Provider value={ctx}>
      <PLCProvider
        handles={handles}
        plcUrl={plcUrl}
        pressId={pressId}
        debugPaths={debugPaths}
        test={test}
      >
        {children}
      </PLCProvider>
    </HMIHandleContext.Provider>
  );
}

HMIHandleProvider.propTypes = {
  plcUrl: PropTypes.string.isRequired,
  pressId: PropTypes.string.isRequired,
  /** The schema's *_GROUPS map: { groupName: handle[] }. */
  groupsMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }))).isRequired,
  debugPaths: PropTypes.shape({}), // optional object of pathName: pathObject for test/dev debugging
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  test: PropTypes.bool,
};

export const useHMIHandles = () => {
  const ctx = useContext(HMIHandleContext);
  if (!ctx) throw new Error('useHMIHandles must be used within HMIHandleProvider');
  return ctx;
};
